import './input-checkbox.scss';

import { ComponentPropsWithRef, useCallback } from 'react';
import { useIntl } from 'react-intl';

import { formatTestId } from '../../../data/utils/string.utils';
import { BaseInput } from './base-input';
import { BaseSwitch } from './base-switch';

type InputSwitch = Omit<
  ComponentPropsWithRef<typeof BaseInput>,
  'value' | 'onChange'
> & {
  className?: string;
  label?: JSX.Element | string;
  description?: JSX.Element | string;
  title?: JSX.Element | string | number;
  error?: string | { error: string; [key: string]: string };
  value?: boolean;
  disabled?: boolean;
  onChange?: (value: boolean) => void;
};

export function InputSwitch({
  testId,
  className,
  label,
  description,
  title,
  error,
  value,
  disabled = false,
  onChange,
  ...props
}: InputSwitch) {
  const intl = useIntl();

  const getTestId = useCallback(
    (testIdType: string | string[] | null = null) =>
      formatTestId('input-switch', testId ?? props.name, testIdType),
    [testId, props.name]
  );

  return (
    <div
      data-test-id={getTestId()}
      className={`form-group form-group--switch${
        className ? ` form-group--${className}` : ''
      }${error ? ' invalid' : ''}${disabled ? ' disabled' : ''}`}
    >
      {label && (
        <label data-test-id={getTestId('label')} htmlFor={props.name}>
          {label}
        </label>
      )}

      {description && (
        <div
          data-test-id={getTestId('description')}
          className="input-description"
        >
          {description}
        </div>
      )}

      <div className="input-group">
        <BaseSwitch
          testId={getTestId('switch')}
          value={value}
          title={title}
          disabled={disabled}
          onChange={onChange}
        />

        {error && (
          <div data-test-id={getTestId('error')} className="input-error">
            {intl.formatMessage(
              {
                id: typeof error === 'string' ? error : error.error,
              },
              typeof error === 'string' ? {} : error
            )}
          </div>
        )}
      </div>
    </div>
  );
}
